// src/components/admin/dashboard/WorkspaceCard.tsx
import { Link } from '@tanstack/react-router';
import { ArrowUpRight, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface WorkspaceCardProps {
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
  variant: 'platform' | 'workspace';
}

const ICON_COLOR = {
  platform: 'text-primary-500',
  workspace: 'text-accent-ink',
} as const;

export function WorkspaceCard({ to, icon: Icon, title, description, variant }: WorkspaceCardProps) {
  return (
    <Link
      to={to as never}
      className={cn(
        'group bg-surface relative flex h-36 flex-col gap-3 rounded-xl border border-(--color-border) p-4 shadow-xs',
        'transition-all duration-200 ease-out',
        'hover:border-primary-200 hover:-translate-y-0.5 hover:shadow-md',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        'motion-reduce:transform-none motion-reduce:hover:translate-y-0',
      )}
    >
      <div className="flex items-start justify-between">
        <div
          className={cn(
            'bg-primary-50 flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
            'group-hover:bg-primary-100',
            ICON_COLOR[variant],
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
        </div>
        <ArrowUpRight
          className={cn(
            'h-4 w-4 text-neutral-300 transition-all duration-200 ease-out',
            'group-hover:text-primary-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5',
            'motion-reduce:transition-none',
          )}
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </div>

      <div className="mt-auto min-w-0">
        <h3 className="text-body-md text-accent-ink truncate leading-tight font-semibold">
          {title}
        </h3>
        <p className="text-meta-sm mt-1 line-clamp-2 leading-snug text-neutral-500">
          {description}
        </p>
      </div>
    </Link>
  );
}
